import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowLeft, LogOut, User } from "lucide-react"; // logout icon
import cdacLogo from "@assets/images/cdac_logo.png"; // adjust path
import bharati from "@assets/images/bharati.png"; // adjust path
import emblem from "@assets/images/Group.png"; // adjust path
import { useNavigate } from "react-router-dom";
import { logoutUser } from "@/store/thunks/studentLogin";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from "lodash";
import { BASE_URL } from "@/utils/constants";
import apiClient from "@/services/api/apiClientStudent";
import { setNotifications, setUnreadCount } from "@/store/slices/studentDashboard";
import { setSelectedLanguage } from "@/store/slices/loginSlice";

const Header = () => {
    const { isAuthenticated, userId, token, role } = useSelector((state) => state.studentAuth);
    const [hovered, setHovered] = useState(false);
    const [profileHovered, setProfileHovered] = useState(false);
    const [backHovered, setBackHovered] = useState(false);
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
    const { notifications, unreadCount, selectedModule } = useSelector((state) => state.studentDashboard);
    const { selectedLanguage } = useSelector((state) => state.loginPage);
    const [searchQuery, setSearchQuery] = useState("");
    const [inputValue, setInputValue] = useState("");
    const [showNotifications, setShowNotifications] = useState(false);
    const [notificationType, setNotificationType] = useState("ALL");
   
    const debouncedSetSearchQuery = useCallback(
        debounce((value) => {
            setSearchQuery(value);
        }, 150),
        []
    );

      useEffect(() => {
    document.body.style.overflow = showLogoutConfirm ? "hidden" : "auto";
  }, [showLogoutConfirm]);

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const handleBack = () => {
        navigate(-1);
    };

    const handleProfile = () => {
        navigate("/student/my-profile"); // route to profile page
    };

    const handleHome = () => {
        if (role === 'STUDENT') {
            navigate("/student"); // route to profile page
        } else {
            navigate("/reviewer");
        }
    };


    // Handle input change
    const handleInputChange = (e) => {
        const value = e.target.value;
        setInputValue(value);
        debouncedSetSearchQuery(value);
    };


    const profilePage = location.pathname === `/student/my-profile`;
    const isDashbaordPage = location.pathname === `/student` || location.pathname === `/reviewer`
    const loginPage = location.pathname === '/register' || location.pathname === '/about-sangraha' || location.pathname === '/';

    useEffect(() => {
        if (selectedModule && !isDashbaordPage) {
            setNotificationType("ALL");
        }
    }, [selectedModule, isDashbaordPage]);

    const filteredNotifications = useMemo(() => {
        let filtered = notifications;
        console.log(selectedModule)

        /* ----------------------------------
           1️⃣ MODULE FILTER (highest priority)
        ---------------------------------- */

        // If user is inside a module (Dictionary, OCR, etc)
        if (selectedModule && selectedModule !== "ALL") {
            filtered = filtered.filter(
                notif => notif.moduleType === selectedModule
            );
        }

        // Dashboard module tabs (TTT, OCR, etc)
        if (isDashbaordPage && notificationType !== "ALL") {
            filtered = filtered.filter(
                notif => notif.moduleType === notificationType
            );
        }

        /* ----------------------------------
           2️⃣ NOTIFICATION TYPE FILTER
        ---------------------------------- */

        if (!isDashbaordPage && notificationType !== "ALL") {
            filtered = filtered.filter(
                notif => notif.notificationType === notificationType
            );
        }

        /* ----------------------------------
           3️⃣ SEARCH FILTER
        ---------------------------------- */

        if (!searchQuery) return filtered;

        const query = searchQuery.toLowerCase();

        return filtered.filter(
            notif =>
                notif.title?.toLowerCase().includes(query) ||
                notif.message?.toLowerCase().includes(query)
        );
    }, [
        notifications,
        searchQuery,
        notificationType,
        selectedModule,
        isDashbaordPage
    ]);


    const handleOpenNotifications = async () => {
        console.log("Auth State on Open Notifications:", { isAuthenticated, });
        if (!isAuthenticated) return;
        setShowNotifications(true);
        setSearchQuery("");
        setInputValue("");
        setNotificationType("ALL");
        try {
            console.log("🔔 handleOpenNotifications called");
            const data = await apiClient(`${BASE_URL}api/notification/fetch?userId=${userId}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                credentials: "include",
            }, navigate);
            console.log("Fetched notifications:", data);
            if (data.status && Array.isArray(data.data)) {
                dispatch(setNotifications(data.data));
                dispatch(setUnreadCount(data.data.filter((n) => !n.isRead).length));
            } else {
                console.error("Invalid notification data:", data);
                dispatch(setNotifications([]));
                dispatch(setUnreadCount(0));
            }
        } catch (err) {
            console.error("Error fetching notifications:", err);
            dispatch(setNotifications([]));
            dispatch(setUnreadCount(0));
        }
    };

    const handleCloseNotifications = () => {
        setShowNotifications(false);
        setSearchQuery("");
        setInputValue("");
        setNotificationType("ALL");
    };

    const handleMarkAsRead = async (notificationId) => {
        console.log(notificationId)
        const notification = notifications.find(n => n.notificationId === notificationId);

        // Do nothing if the notification is already read
        if (!notification || notification.isRead) {
            return;
        }

        // Optimistic UI Update: Update the state immediately
        const originalNotifications = [...notifications];
        const updatedNotifications = notifications.map(n =>
            n.notificationId === notificationId ? { ...n, isRead: true } : n
        );
        dispatch(setNotifications(updatedNotifications));
        dispatch(setUnreadCount(unreadCount - 1));

        try {
            // API Call to update the backend
            const response = await apiClient(`${BASE_URL}api/notification/read?notificationId=${notificationId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    "Authorization": `Bearer ${token}`
                },
                credentials: "include",
            }, navigate);

        } catch (error) {
            console.error('Error marking notification as read:', error);
            // If the API call fails, revert the UI to its original state
            dispatch(setNotifications(originalNotifications));
            dispatch(setUnreadCount(unreadCount));
            // Optionally, show an error message to the user here
        }
    };

    const moveToBharati = () => {
        window.open("https://bharati.rajbhasha.gov.in/", "_blank");
    }

    const moveToCDAC = () => {
        window.open("https://www.cdac.in/", "_blank");
    }


    return (
        <header className="w-full bg-[rgba(255,255,255,1)]">
            {/* Top Section */}
            <div className="w-full flex items-center justify-between px-5 py-2">
                {/* Left Section */}
                <div className="flex items-center gap-4">
                    <img src={emblem} alt="CDAC" className="h-16" />
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800">राजभाषा विभाग</h1>
                        <h2 className="text-lg text-gray-600">Department of Official Language</h2>
                    </div>
                </div>

                {/* Middle Section */}
                <div className="flex flex-col text-center">
                    <h1 className="text-3xl font-bold text-[rgba(236,112,38,1)]">भारती संग्रह</h1>
                    <h2 className="text-xl text-[rgba(236,112,38,1)]">Bharati Sangraha</h2>
                </div>

                {/* Right Section */}
                <div className="flex items-center gap-5">
                    {loginPage && <div className="flex items-center gap-2">
                        <select
                            value={selectedLanguage}
                            onChange={(e) => {
                                dispatch(setSelectedLanguage(e.target.value));
                            }}
                            className="px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 h-[40px] focus:ring-[#ec7026] focus:outline-none"
                        >
                            <option value="english">English</option>
                            <option value="hindi">हिंदी</option>
                        </select>

                    </div>}
                    <img src={bharati} alt="Bharati Logo" className="h-[80px] hover:cursor-pointer" onClick={moveToBharati} />
                    <div className="w-[2px] h-[60px] bg-[rgba(0,0,0,0.18)]"></div>
                    <img src={cdacLogo} alt="CDAC Logo" className="h-16 hover:cursor-pointer" onClick={moveToCDAC} />
                </div>
            </div>

            {/* Bottom Section */}
            {!loginPage && <div className="w-full flex items-center px-5 py-2 border-t">
                {/* Back Button */}
                {!isDashbaordPage && (
                    <button
                        onMouseEnter={() => setBackHovered(true)}
                        onMouseLeave={() => setBackHovered(false)}
                        onClick={handleBack}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium transition-all"
                    >
                        <ArrowLeft
                            size={18}
                            className={`transition-transform duration-200 ${backHovered ? "-translate-x-1" : ""}`}
                        />
                        Back
                    </button>
                )}

                {/* This pushes the right buttons fully to the right */}
                <div className="flex items-center gap-4 ml-auto">
                    {/* My Profile / Go To Home */}
                    {/* Notifications Button */}
                    <div className="relative">
                        <button
                            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#ec7026] transition-colors duration-200"
                            onClick={handleOpenNotifications}
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                                />
                            </svg>
                            {unreadCount > 0 && (
                                <span className="absolute -top-1 -right-1 flex h-5 w-5">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#ec7026] opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-5 w-5 bg-[#ec7026] text-white text-xs items-center justify-center font-['Semantic/Strong']">
                                        {unreadCount}
                                    </span>
                                </span>
                            )}
                        </button>
                    </div>
                    {showNotifications && isDashbaordPage && (
                        <div
                            className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-40"
                            onClick={handleCloseNotifications} // Close when clicking outside
                        >
                            <div
                                className="w-[40rem] bg-white border border-gray-200 rounded-2xl shadow-2xl p-4 animate-fadeIn max-h-[75vh] overflow-y-auto"
                                onClick={(e) => e.stopPropagation()} // Prevent close when clicking inside
                            >

                                {/* Header */}
                                <div className="flex items-center justify-between px-2 pb-3 border-b border-gray-200">
                                    <h2 className="text-lg font-semibold text-gray-800 tracking-wide">
                                        🔔 Notifications
                                    </h2>
                                    <button
                                        onClick={handleCloseNotifications}
                                        className="text-gray-500 hover:text-gray-800 text-lg transition-all"
                                    >
                                        ✕
                                    </button>
                                </div>

                                {/* Filter Tabs */}
                                <div className="flex justify-center gap-1 py-3 border-b border-gray-100 w-full">
                                    {["ALL", "TTT", "TTS / ASR","DICTIONARY"].map(
                                        (type) => (
                                            <button
                                                key={type}
                                                onClick={() => setNotificationType(type === 'TTS / ASR' ? 'TS' : type === 'TTT' ? 'TT' : type)}
                                                className={`px-4 py-1.5 rounded-full text-sm font-medium shadow-sm transition-all duration-200
                                                 ${notificationType === type
                                                        ? "bg-[#ec7026] text-white shadow-md"
                                                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                                    }`}
                                            >
                                                {type === "ALL" ? "All" : type}
                                            </button>
                                        )
                                    )}

                                </div>

                                {/* Search Input */}
                                <div className="px-3 py-3">
                                    <input
                                        type="text"
                                        value={inputValue}
                                        onChange={handleInputChange}
                                        placeholder="🔍  Search notifications..."
                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#ec7026] focus:border-[#ec7026] outline-none transition-all"
                                    />
                                </div>

                                {/* Notification List */}
                                <ul className="px-2 space-y-3 mt-1">
                                    {filteredNotifications.length > 0 ? (
                                        filteredNotifications.map((notif) => (
                                            <li
                                                key={notif.notificationId}
                                                onClick={() => handleMarkAsRead(notif.notificationId)}
                                                className={`p-4 rounded-xl border cursor-pointer transition-all duration-200
                                                    ${notif.isRead
                                                        ? "bg-white border-gray-200 hover:bg-gray-50"
                                                        : "bg-orange-50 border-orange-200 hover:bg-orange-100"
                                                    }`}
                                            >
                                                {/* Badges */}
                                                <div className="flex flex-wrap gap-2 mb-2">
                                                    <span
                                                        className={`px-3 py-0.5 text-xs font-semibold rounded-full
                                                            ${notif.notificationType === "ALERT"
                                                                ? "bg-red-100 text-red-700"
                                                                : notif.notificationType === "INFO"
                                                                    ? "bg-blue-100 text-blue-700"
                                                                    : notif.notificationType === "SUCCESS"
                                                                        ? "bg-green-100 text-green-700"
                                                                        : "bg-gray-100 text-gray-700"
                                                            }`}
                                                    >
                                                        {notif.notificationType}
                                                    </span>

                                                    <span className="px-3 py-0.5 text-xs font-medium rounded-full border border-gray-300 text-gray-600 bg-white">
                                                        {notif.moduleType}
                                                    </span>
                                                </div>

                                                {/* Title */}
                                                <p className="font-semibold text-gray-900 text-base mb-1">
                                                    {notif.title}
                                                </p>

                                                {/* Message */}
                                                <p className="text-gray-700 text-sm leading-relaxed">
                                                    {notif.message}
                                                </p>

                                                {/* Time */}
                                                <p className="text-gray-400 text-xs mt-2 italic">
                                                    {new Date(notif.createdAt).toLocaleString()}
                                                </p>
                                            </li>
                                        ))
                                    ) : (
                                        <li className="text-center text-gray-500 text-sm py-6">
                                            No notifications available
                                        </li>
                                    )}
                                </ul>

                            </div>
                        </div>
                    )}

                    {showNotifications && !isDashbaordPage && (
                        <div
                            className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-40"
                            onClick={handleCloseNotifications} // Close when clicking outside
                        >
                            <div
                                className="w-[45rem] bg-white border border-gray-200 rounded-2xl shadow-2xl p-4 animate-fadeIn max-h-[75vh] overflow-y-auto"
                                onClick={(e) => e.stopPropagation()} // Prevent close when clicking inside
                            >

                                {/* Header */}
                                <div className="flex items-center justify-between px-2 pb-3 border-b border-gray-200">
                                    <h2 className="text-lg font-semibold text-gray-800 tracking-wide">
                                        🔔 Notifications
                                    </h2>
                                    <button
                                        onClick={handleCloseNotifications}
                                        className="text-gray-500 hover:text-gray-800 text-lg transition-all"
                                    >
                                        ✕
                                    </button>
                                </div>

                                {/* Filter Tabs */}
                                <div className="flex justify-center gap-1 py-3 border-b border-gray-100 w-full">
                                    {["ALL", "SUBMITTED", "ASSIGNMENT", "REJECT", "INFO", "RESUBMITTED", "ALERT"].map(
                                        (type) => (
                                            <button
                                                key={type}
                                                onClick={() => setNotificationType(type)}
                                                className={`px-4 py-1.5 rounded-full text-sm font-medium shadow-sm transition-all duration-200
        ${notificationType === type
                                                        ? "bg-[#ec7026] text-white shadow-md"
                                                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                                    }`}
                                            >
                                                {type === "ALL" ? "All" : type}
                                            </button>
                                        )
                                    )}

                                </div>

                                {/* Search Input */}
                                <div className="px-3 py-3">
                                    <input
                                        type="text"
                                        value={inputValue}
                                        onChange={handleInputChange}
                                        placeholder="🔍  Search notifications..."
                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#ec7026] focus:border-[#ec7026] outline-none transition-all"
                                    />
                                </div>

                                {/* Notification List */}
                                <ul className="px-2 space-y-3 mt-1">
                                    {filteredNotifications.length > 0 ? (
                                        filteredNotifications.map((notif) => (
                                            <li
                                                key={notif.notificationId}
                                                onClick={() => handleMarkAsRead(notif.notificationId)}
                                                className={`p-4 rounded-xl border cursor-pointer transition-all duration-200
                                                    ${notif.isRead
                                                        ? "bg-white border-gray-200 hover:bg-gray-50"
                                                        : "bg-orange-50 border-orange-200 hover:bg-orange-100"
                                                    }`}
                                            >
                                                {/* Badges */}
                                                <div className="flex flex-wrap gap-2 mb-2">
                                                    <span
                                                        className={`px-3 py-0.5 text-xs font-semibold rounded-full
                                                            ${notif.notificationType === "ALERT"
                                                                ? "bg-red-100 text-red-700"
                                                                : notif.notificationType === "INFO"
                                                                    ? "bg-blue-100 text-blue-700"
                                                                    : notif.notificationType === "SUCCESS"
                                                                        ? "bg-green-100 text-green-700"
                                                                        : "bg-gray-100 text-gray-700"
                                                            }`}
                                                    >
                                                        {notif.notificationType}
                                                    </span>

                                                    <span className="px-3 py-0.5 text-xs font-medium rounded-full border border-gray-300 text-gray-600 bg-white">
                                                        {notif.moduleType}
                                                    </span>
                                                </div>

                                                {/* Title */}
                                                <p className="font-semibold text-gray-900 text-base mb-1">
                                                    {notif.title}
                                                </p>

                                                {/* Message */}
                                                <p className="text-gray-700 text-sm leading-relaxed">
                                                    {notif.message}
                                                </p>

                                                {/* Time */}
                                                <p className="text-gray-400 text-xs mt-2 italic">
                                                    {new Date(notif.createdAt).toLocaleString()}
                                                </p>
                                            </li>
                                        ))
                                    ) : (
                                        <li className="text-center text-gray-500 text-sm py-6">
                                            No notifications available
                                        </li>
                                    )}
                                </ul>

                            </div>
                        </div>
                    )}

                    {!profilePage ? (
                        <button
                            onMouseEnter={() => setProfileHovered(true)}
                            onMouseLeave={() => setProfileHovered(false)}
                            onClick={handleProfile}
                            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-700 text-sm font-medium transition-all"
                        >
                            <User
                                size={18}
                                className={`transition-transform duration-200 ${profileHovered ? "scale-110" : ""}`}
                            />
                            My Profile
                        </button>
                    ) : (
                        <button
                            onClick={handleHome}
                            className="px-3 py-2 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-700 text-sm font-medium transition-all"
                        >
                            Go To Home
                        </button>
                    )}

                    {/* Logout Button */}
                    <button
                        onMouseEnter={() => setHovered(true)}
                        onMouseLeave={() => setHovered(false)}
                        onClick={() => setShowLogoutConfirm(true)}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-semibold transition-all"
                    >
                        <LogOut
                            size={18}
                            className={`transition-transform duration-200 ${hovered ? "rotate-180" : ""}`}
                        />
                        Logout
                    </button>
                </div>
            </div>}

            {showLogoutConfirm && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm">

              <h2 className="text-lg font-bold text-gray-900 mb-2">
                Confirm Logout
              </h2>

              <p className="text-sm text-gray-600 mb-6">
                Are you sure you want to logout?
              </p>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowLogoutConfirm(false)}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-gray-700"
                >
                  Cancel
                </button>

                <button
                  onClick={() => {
                    setShowLogoutConfirm(false);
                    dispatch(logoutUser());
                    navigate("/", { replace: true });
                  }}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg"
                >
                  Logout
                </button>
              </div>

            </div>
          </div>
        )}

        </header>

    );
};

export default Header;
